import BarChartViz from '../vizs/BarChartViz'
import BaseComponent from './BaseComponent'
/**
 * {-EN-}
 * # {componentLabel} (`class {componentClassName}`)
 * The stacked bar chart is used to display values across two or more categorical variables. Each bar is divided into a number of sub-bars stacked end to end, each one corresponding to a level of the second categorical variable.
 *
 * ### Example Usage
 * ```javascript
 *
 *   import {StackedBars} from 'sdv-lib'
 *
 *   // Instantiate the chart with a DOM element, a data Array and a config Object
 *   let myChart = new StackedBars($el, data, config);
 *
 * ```
 *
 * ### Data structure {.section-data-structure}
 * The tabular data for this chart should be a table with 3 or more columns.
 *
 * Each row represents a stack. The first column should be the id/label of the stack, and the rest of the columns should represent each of the layers in the stack.
 *
 * | category   | value1 |  value2  |  value3  |
 * | --------   | ------ |  ------  |  ------  |
 * |   Jan      |    22.4|      15.4|      43.2|
 * |   Feb      |    52.3|      29.3|      12.8|
 * |   Mar      |    12.8|      34.1|      61.4|
 * |   Apr      |    26.0|      12.3|      14.5|
 *
 * {.data-table}
 *
 * Each layer will be assigned a different color, to distinguish them inside of each stack.
 *
 * {-ES-}
 * # {componentLabelEs} (`class {componentClassName}`)
 * El gráfico de barras apiladas se utiliza para mostrar valores en dos o más variables categóricas. Cada barra se divide en una serie de subbarras apiladas de un extremo a otro, cada una de las cuales corresponde a un nivel de la segunda variable categórica.
 *
 * ### Ejemplo de uso
 * ```javascript
 *
 *   import {StackedBars} from 'sdv-lib'
 *
 *   // Instantiate the chart with a DOM element, a data Array and a config Object
 *   let myChart = new StackedBars($el, data, config);
 *
 * ```
 *
 * ### Estructura de datos {.section-data-structure}
 * Los datos tabulares de este gráfico deben ser una tabla con 3 o más columnas.
 *
 * Cada fila representa una pila. La primera columna debe ser la identificación/etiqueta de la pila, y el resto de las columnas deben representar cada una de las capas de la pila.
 *
 * | categoría   | valor1 |  valor2  |  valor3  |
 * | --------   | ------ |  ------  |  ------  |
 * |   Jan      |    22.4|      15.4|      43.2|
 * |   Feb      |    52.3|      29.3|      12.8|
 * |   Mar      |    12.8|      34.1|      61.4|
 * |   Apr      |    26.0|      12.3|      14.5|
 *
 * {.data-table}
 *
 * A cada capa se le asignará un color diferente, para distinguirlas dentro de cada pila.
 *
 * */
export default class StackedBarsComponent extends BaseComponent {
  static vizClass = BarChartViz
  static componentLabel = 'Stacked Bar chart'
  static componentLabelEs = 'Gráfico de barras apiladas'
  static dataFormat = [
    { index: 0, axis: 'x0', types: [String], role: 'id', roleLabel: 'Category' },
    {
      index: -1,
      axis: 'y0',
      label: 'Stack',
      types: [Number, null],
      role: 'value',
      roleLabel: 'Stacked value',
    },
  ]
  static defaultConfig = {
    title: this.componentLabel,
    mode: 'stacked',
    ItemsHelper: {
      colorScale: {
        value: 'categorical',
        options: ['categorical'],
        nonEditable: true,
      },
    },
    /**
     * {-EN-} Show the TOTAL value on top of each stack.
     * {-ES-} Muestre el valor TOTAL en la parte superior de cada pila.
     */
    showDatumValues: {
      type: Boolean,
      default: false,
      exposed: true,
    },
    AxisHelper: {
      y: {
        trimThreshold: {
          nonEditable: true,
        },
      },
      x: {
        showTickValues: {
          nonEditable: false,
        },
      },
    },
    TooltipBehavior: {
      axis: {
        y: {
          tooltipValueFormat: {
            exposed: true,
            nonEditable: false,
          },
        },
      },
      getHtml: function (d) {
        return `
        <div class="tip-value important">${this.formatTooltipValue(d.value, 'y')}</div>
        <div class="tip-id">
          <span class="icon" style="display: inline-block; width: 9px; height: 9px; border-radius: 50%; background: ${this.color(
            { id: d.parentId }
          )}; margin-right: 3px"></span>
          ${d.parentId}
        </div>
        <div class="secondary">${this.formatValue(d.id, 'x')}</div>
        `
      },
    },
  }
  static dataRequirements = {
    recomendedMaxRows: 10,
    recomendedMinCols: 2,
    maxRows: 30,
    minCols: 2,
    sign: 'positive', //'negative','any','both'
    requirementsStrength: 10,
  }

  static accessibilityRules = {
    ...super.accessibilityRules,
    chartComplexity: {
      ...super.accessibilityRules.chartComplexity,
      label: 'rule_limit_stack_complexity_label',
      description: 'rule_limit_stack_complexity_desc',
      check: (config, data) => {
        const MAX_DATA_COMPLEXITY = 4
        return data.length <= MAX_DATA_COMPLEXITY
      },
    },
  }
}
